/**
 * Menu — the classic Pokémon selection box: a bordered cream box listing options,
 * with a ▶ triangular cursor that moves to the highlighted row. Works by KEYBOARD
 * (↑/↓ to move, Enter/Space to choose, Esc to cancel), TOUCH/MOUSE (tap a row to
 * choose; tapping moves the cursor first on the way down), and is fully accessible —
 * no hover-only paths. A tasteful GB/GBC nod over a clean, readable font.
 *
 * Used for FIGHT/BAG/POKEMON/RUN, move lists, greeting choices, and Title/NewGame
 * options. Button still exists for one-off CTAs; Menu is the multi-option workhorse.
 *
 *   const m = new Menu(scene, x, y, {
 *     items: [{ label: 'Fight', onSelect: ... }, { label: 'Run', onSelect: ... }],
 *     width: 160,
 *   });
 *   m.focus();                 // start taking keyboard input (call when shown)
 *
 * The cursor math is pure (./menuNav) so it is unit-tested independently of Phaser.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET } from './theme.js';
import { clampIndex, firstSelectable, moveCursor, type MenuDir } from './menuNav.js';
import { domTextFieldFocused } from './keyboardGate.js';
import { makeClickable } from './clickable.js';
import { clampScroll, isDragGesture, isScrollable, thumbGeometry } from './scroll.js';

export interface MenuItem {
  readonly label: string;
  /** Chosen via Enter/Space or a tap. */
  readonly onSelect: () => void;
  /** A small right-aligned annotation (e.g. "PP 12/15", a price). Optional. */
  readonly hint?: string;
  /** Greyed + unselectable (cursor skips it). */
  readonly disabled?: boolean;
}

export interface MenuOptions {
  readonly items: readonly MenuItem[];
  /** Fixed box width; defaults to fit the widest label + cursor + padding. */
  readonly width?: number;
  /** Per-row height; defaults to a comfortable touch target. */
  readonly rowHeight?: number;
  readonly fontSize?: number;
  /** Index to seat the cursor on initially (skips to the next selectable). */
  readonly initialIndex?: number;
  /** Called on Esc / cancel gesture, if provided. */
  readonly onCancel?: () => void;
  /** Disable the gentle cursor-blink (mirrors Settings.reducedMotion). */
  readonly reducedMotion?: boolean;
  /** Columns to lay the items out in (1 = classic vertical list; 2 = the FIGHT grid). */
  readonly columns?: number;
  /**
   * Cap the visible rows: when the item list is longer, the menu becomes SCROLLABLE — the
   * box height is capped to this many rows, content is clipped to the window, the ▶ cursor
   * auto-scrolls into view (keyboard), and touch-drag / mouse-wheel scroll the list. Only
   * honored for a single-column menu (`columns` 1/undefined); a multi-column grid ignores
   * it. Used by the unlimited-moveset FIGHT menu so a long moveset stays usable on a phone.
   */
  readonly maxVisibleRows?: number;
}

const PAD_X = SPACING.md;
const CURSOR_W = 14;

export class Menu extends Phaser.GameObjects.Container {
  private readonly items: readonly MenuItem[];
  private readonly disabledSet: ReadonlySet<number>;
  private readonly rows: { bg: Phaser.GameObjects.Graphics; y: number; x: number }[] = [];
  private readonly cursor: Phaser.GameObjects.Text;
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly cols: number;
  private readonly rowH: number;
  private readonly colW: number;
  private readonly onCancel?: () => void;
  private readonly reducedMotion: boolean;
  private readonly keyHandler: (e: KeyboardEvent) => void;
  private index = 0;
  private focused = false;
  private blink?: Phaser.Time.TimerEvent;

  // ── Scrolling (only when maxVisibleRows caps a single-column list) ──────────────
  /** True when the list overflows its capped window and scroll/drag is meaningful. */
  private readonly scrollable: boolean;
  /** Total px of laid-out content (all rows) — the scroll content height. */
  private readonly contentHeight: number;
  /** The inner container holding all rows + the cursor; translated by the scroll offset. */
  private readonly viewport?: Phaser.GameObjects.Container;
  private readonly maskGfx?: Phaser.GameObjects.Graphics;
  private readonly thumb?: Phaser.GameObjects.Graphics;
  private scrollOffset = 0;
  private pressed = false;
  private gestureDragged = false;
  private startPointerX = 0;
  private startPointerY = 0;
  private lastPointerY = 0;
  private readonly onDownH = (p: Phaser.Input.Pointer): void => this.onScrollDown(p);
  private readonly onMoveH = (p: Phaser.Input.Pointer): void => this.onScrollMove(p);
  private readonly onUpH = (): void => {
    this.pressed = false;
  };
  private readonly onWheelH = (
    p: Phaser.Input.Pointer,
    _o: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => this.onScrollWheel(p, dy);

  /** Box dimensions (so callers can position it precisely). */
  readonly boxWidth: number;
  readonly boxHeight: number;

  constructor(scene: Phaser.Scene, x: number, y: number, options: MenuOptions) {
    super(scene, x, y);
    this.items = options.items;
    this.onCancel = options.onCancel;
    this.reducedMotion = options.reducedMotion ?? false;
    this.cols = Math.max(1, options.columns ?? 1);
    const fontSize = options.fontSize ?? FONT.size.md;
    this.rowH = options.rowHeight ?? TOUCH_TARGET;

    const disabled = new Set<number>();
    this.items.forEach((it, i) => {
      if (it.disabled) disabled.add(i);
    });
    this.disabledSet = disabled;

    const rowCount = Math.ceil(this.items.length / this.cols);
    // Width: fit the widest label (incl. hint) + cursor gutter + padding.
    const probe = scene.add
      .text(0, 0, '', { fontFamily: FONT.family, fontSize: `${fontSize}px` })
      .setVisible(false);
    let widest = 0;
    for (const it of this.items) {
      probe.setText(it.hint ? `${it.label}   ${it.hint}` : it.label);
      widest = Math.max(widest, probe.width);
    }
    probe.destroy();
    // `widest` is the widest single CELL (label + hint). Each column must fit a whole
    // cell — dividing it by cols (the old bug) made 2-column cells half-width, so the
    // left label and right-aligned hint (e.g. the battle move PP) overlapped.
    const cellW = Math.max(TOUCH_TARGET * 1.6, widest + CURSOR_W + PAD_X * 2 + 6);
    this.colW = options.width ? options.width / this.cols : cellW;
    this.boxWidth = options.width ?? this.colW * this.cols;

    // Scrolling is honored ONLY for a single-column list that overflows the cap.
    const cap = options.maxVisibleRows;
    this.contentHeight = rowCount * this.rowH + SPACING.sm * 2;
    this.scrollable = this.cols === 1 && cap !== undefined && rowCount > cap;
    this.boxHeight = this.scrollable ? cap! * this.rowH + SPACING.sm * 2 : this.contentHeight;

    this.gfx = scene.add.graphics();
    this.add(this.gfx);
    this.drawBox();

    // Rows + cursor live in a viewport container so a scrolling menu can translate them
    // as one and clip to the window. A non-scrolling menu uses `this` directly (offset 0),
    // so nothing changes for the many existing callers.
    if (this.scrollable) {
      this.viewport = scene.add.container(0, 0);
      this.add(this.viewport);
    }
    const host: Phaser.GameObjects.Container = this.viewport ?? this;

    // Per-row highlight + label + hint. Rows are laid out against the CONTENT box height
    // (so a scrolling list lays all rows out, then clips); the box-top alignment is the
    // same as before for a non-scrolling list (content === box height there).
    const layoutTop = -this.contentHeight / 2;
    // For a scrolling list, the viewport is anchored to the box top; shift rows so the
    // first row sits just inside the (shorter) box top, then scroll from there.
    const rowsTopShift = this.scrollable ? this.contentHeight / 2 - this.boxHeight / 2 : 0;
    this.items.forEach((it, i) => {
      const col = i % this.cols;
      const row = Math.floor(i / this.cols);
      const rx = -this.boxWidth / 2 + col * this.colW;
      const ry = layoutTop + SPACING.sm + row * this.rowH + rowsTopShift;
      const cy = ry + this.rowH / 2;

      const bg = scene.add.graphics();
      host.add(bg);
      this.rows.push({ bg, y: cy, x: rx });

      const label = scene.add
        .text(rx + CURSOR_W + PAD_X, cy, it.label, {
          fontFamily: FONT.family,
          fontSize: `${fontSize}px`,
          color: it.disabled ? CSS.boxTextMuted : CSS.boxText,
          fontStyle: it.disabled ? 'italic' : 'normal',
        })
        .setOrigin(0, 0.5);
      host.add(label);

      if (it.hint) {
        const hint = scene.add
          .text(rx + this.colW - PAD_X, cy, it.hint, {
            fontFamily: FONT.mono,
            fontSize: `${Math.round(fontSize * 0.8)}px`,
            color: CSS.boxTextMuted,
          })
          .setOrigin(1, 0.5);
        host.add(hint);
      }

      // Whole row is a tap target (touch-first; big targets). The zone is centered on
      // (rx + colW/2, cy) — the middle of the drawn row band — and makeClickable seats a
      // hit rect that accounts for the zone's displayOrigin (w/2,h/2) so the clickable
      // band lines up exactly with the label/highlight. A pointer cursor turns the mouse
      // into a hand over selectable rows; a disabled row gets no cursor (not a target).
      const hit = scene.add.zone(rx + this.colW / 2, cy, this.colW, this.rowH);
      if (it.disabled) {
        makeClickable(hit, this.colW, this.rowH, { cursor: 'default' });
      } else if (this.scrollable) {
        // SCROLLABLE: rows select on POINTER_UP guarded by the drag flag (mirrors
        // ScrollList) so a touch-drag that STARTS over a row scrolls the list instead of
        // selecting it. makeClickable seats the hit area + hand cursor (no down-handler).
        makeClickable(hit, this.colW, this.rowH);
        hit.on(Phaser.Input.Events.POINTER_UP, () => {
          if (this.gestureDragged) return;
          this.tapRow(i);
        });
      } else {
        // Non-scrolling menus keep the classic POINTER_DOWN select (unchanged behavior).
        makeClickable(hit, this.colW, this.rowH, {
          onClick: () => this.tapRow(i),
        });
      }
      host.add(hit);
    });

    // The ▶ triangular selection cursor (inside the viewport so it scrolls with its row).
    this.cursor = scene.add
      .text(0, 0, '▶', {
        fontFamily: FONT.family,
        fontSize: `${Math.round(fontSize * 0.9)}px`,
        color: CSS.boxText,
      })
      .setOrigin(0, 0.5);
    host.add(this.cursor);

    if (this.scrollable) {
      // A faint thumb hints scroll position; the geometry mask clips rows to the window.
      this.thumb = scene.add.graphics();
      this.add(this.thumb);
      this.maskGfx = scene.make.graphics({}, false);
      this.applyScrollMask();
      scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.onDownH);
      scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onMoveH);
      scene.input.on(Phaser.Input.Events.POINTER_UP, this.onUpH);
      scene.input.on(Phaser.Input.Events.POINTER_WHEEL, this.onWheelH);
    }

    this.index = firstSelectable(this.items.length, this.disabledSet, options.initialIndex ?? 0);
    this.positionCursor();
    this.scrollCursorIntoView();

    // Keyboard: listen on the DOM (Phaser keyboard plugin is optional in headless).
    this.keyHandler = (e: KeyboardEvent) => this.onKey(e);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    scene.add.existing(this);
  }

  /** Begin taking keyboard input + start the gentle cursor blink. Call when shown. */
  focus(): this {
    if (this.focused) return this;
    this.focused = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.keyHandler);
    }
    if (!this.reducedMotion && this.scene) {
      this.blink = this.scene.time.addEvent({
        delay: 480,
        loop: true,
        callback: () => this.cursor.setVisible(!this.cursor.visible),
      });
    }
    return this;
  }

  /** Stop taking keyboard input + stop blinking (cursor stays visible). */
  blur(): this {
    this.focused = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.blink?.remove();
    this.blink = undefined;
    this.cursor.setVisible(true);
    return this;
  }

  /** The currently-highlighted index. */
  get selectedIndex(): number {
    return this.index;
  }

  /** Move the cursor (programmatic / external d-pad). dir: -1 up, +1 down. */
  move(dir: MenuDir): this {
    this.index = moveCursor(this.index, dir, this.items.length, this.disabledSet);
    this.positionCursor();
    this.scrollCursorIntoView();
    return this;
  }

  /** Choose the current row (programmatic / Enter). No-op on a disabled row. */
  choose(): void {
    const it = this.items[this.index];
    if (!it || it.disabled) return;
    it.onSelect();
  }

  /**
   * Re-apply the scroll mask after the menu is moved. The geometry mask is WORLD-space
   * (it depends on this.x/this.y), and callers construct the menu at (0,0) then reposition
   * it (e.g. BattleScene.positionMenu), so without this the clip window would stay at the
   * origin. A no-op for a non-scrolling menu.
   */
  override setPosition(x?: number, y?: number, z?: number, w?: number): this {
    super.setPosition(x, y, z, w);
    if (this.scrollable) this.applyScrollMask();
    return this;
  }

  override destroy(fromScene?: boolean): void {
    this.blur();
    if (this.scrollable) {
      this.scene?.input?.off(Phaser.Input.Events.POINTER_DOWN, this.onDownH);
      this.scene?.input?.off(Phaser.Input.Events.POINTER_MOVE, this.onMoveH);
      this.scene?.input?.off(Phaser.Input.Events.POINTER_UP, this.onUpH);
      this.scene?.input?.off(Phaser.Input.Events.POINTER_WHEEL, this.onWheelH);
      this.maskGfx?.destroy();
    }
    super.destroy(fromScene);
  }

  private tapRow(i: number): void {
    const it = this.items[i];
    if (!it || it.disabled) return;
    this.index = clampIndex(i, this.items.length);
    this.positionCursor();
    this.scrollCursorIntoView();
    it.onSelect();
  }

  // ── Scroll plumbing (no-ops unless this menu is scrollable) ─────────────────────

  /** Scroll so the highlighted row is fully inside the window (keyboard nav). */
  private scrollCursorIntoView(): void {
    if (!this.scrollable) return;
    const row = this.rows[this.index];
    if (!row) return;
    // Rows live in the viewport; on screen a row at viewport-local `row.y` shows at
    // `row.y - scrollOffset`. Keep its band inside the box window [winTop, winBottom].
    const winTop = -this.boxHeight / 2 + SPACING.sm;
    const winBottom = this.boxHeight / 2 - SPACING.sm;
    const rowTopOnScreen = row.y - this.rowH / 2 - this.scrollOffset;
    const rowBotOnScreen = row.y + this.rowH / 2 - this.scrollOffset;
    if (rowTopOnScreen < winTop) {
      this.setScroll(row.y - this.rowH / 2 - winTop);
    } else if (rowBotOnScreen > winBottom) {
      this.setScroll(row.y + this.rowH / 2 - winBottom);
    } else {
      this.applyScroll();
    }
  }

  private setScroll(offset: number): void {
    // scrollOffset ranges [0, contentHeight - boxHeight]: at 0 the first row's top meets
    // the window top; at max the last row's bottom meets the window bottom (derivation in
    // scrollCursorIntoView). The on-screen y of a row is `row.y - scrollOffset`.
    this.scrollOffset = clampScroll(offset, this.contentHeight, this.boxHeight);
    this.applyScroll();
  }

  private applyScroll(): void {
    if (!this.viewport) return;
    this.viewport.y = -this.scrollOffset;
    this.refreshThumb();
  }

  private applyScrollMask(): void {
    if (!this.maskGfx) return;
    this.maskGfx.clear();
    this.maskGfx.fillStyle(0xffffff, 1);
    // World-space mask aligned to this container's current position + box window.
    this.maskGfx.fillRect(
      this.x - this.boxWidth / 2,
      this.y - this.boxHeight / 2 + SPACING.sm,
      this.boxWidth,
      this.boxHeight - SPACING.sm * 2,
    );
    this.viewport?.setMask(this.maskGfx.createGeometryMask());
  }

  private refreshThumb(): void {
    if (!this.thumb) return;
    const geo = thumbGeometry(this.scrollOffset, this.contentHeight, this.boxHeight);
    this.thumb.clear();
    if (!geo) return;
    const tx = this.boxWidth / 2 - 6;
    const top = -this.boxHeight / 2 + geo.top;
    this.thumb.fillStyle(COLORS.border, 0.7);
    this.thumb.fillRoundedRect(tx, top, 3, geo.height, RADIUS.sm);
  }

  /** The menu's world-space window rect, for bounds-checking scroll gestures. */
  private pointerOverWindow(pointer: Phaser.Input.Pointer): boolean {
    const left = this.x - this.boxWidth / 2;
    const top = this.y - this.boxHeight / 2;
    return (
      pointer.worldX >= left &&
      pointer.worldX <= left + this.boxWidth &&
      pointer.worldY >= top &&
      pointer.worldY <= top + this.boxHeight
    );
  }

  private onScrollDown(pointer: Phaser.Input.Pointer): void {
    // A fresh press always clears the prior gesture's drag flag so a clean tap on a row
    // never inherits a stale "dragged" flag (mirrors ScrollList).
    this.gestureDragged = false;
    if (!this.pointerOverWindow(pointer)) return;
    this.pressed = true;
    this.startPointerX = pointer.x;
    this.startPointerY = pointer.y;
    this.lastPointerY = pointer.y;
  }

  private onScrollMove(pointer: Phaser.Input.Pointer): void {
    if (!this.pressed) return;
    if (
      !this.gestureDragged &&
      !isDragGesture(this.startPointerX, this.startPointerY, pointer.x, pointer.y)
    ) {
      return;
    }
    this.gestureDragged = true;
    const dy = pointer.y - this.lastPointerY;
    // Dragging down reveals earlier content => decrease offset.
    this.setScroll(this.scrollOffset - dy);
    this.lastPointerY = pointer.y;
  }

  private onScrollWheel(pointer: Phaser.Input.Pointer, dy: number): void {
    if (!isScrollable(this.contentHeight, this.boxHeight)) return;
    if (!this.pointerOverWindow(pointer)) return;
    this.setScroll(this.scrollOffset + dy * 0.6);
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.focused) return;
    // Don't steal keys (and don't preventDefault arrows/Enter/Space/Esc) while the
    // player is typing into a focused DOM text field — e.g. the chat TextInput. Without
    // this, menu nav would consume + cancel those keystrokes and they'd never reach the
    // input (dropped letters: space, w/a/s/d, enter while typing).
    if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) {
      return;
    }
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        this.move(-1);
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        this.move(1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.choose();
        break;
      case 'Escape':
        if (this.onCancel) {
          e.preventDefault();
          this.onCancel();
        }
        break;
      default:
        break;
    }
  }

  private positionCursor(): void {
    const row = this.rows[this.index];
    if (!row) return;
    this.cursor.setVisible(true);
    this.cursor.setPosition(row.x + PAD_X - 2, row.y);
    // The classic G/S selection band (soft blue) behind the active row, with a thin
    // dark edge so the highlighted option reads as a boxed selection, not just a tint.
    this.rows.forEach((r, i) => {
      r.bg.clear();
      if (i === this.index) {
        const bx = r.x + 4;
        const by = r.y - this.rowH / 2 + 3;
        const bw = this.colW - 8;
        const bh = this.rowH - 6;
        r.bg.fillStyle(COLORS.boxHighlight, 1);
        r.bg.fillRoundedRect(bx, by, bw, bh, RADIUS.sm);
        r.bg.lineStyle(1, COLORS.accentDeep, 0.9);
        r.bg.strokeRoundedRect(bx, by, bw, bh, RADIUS.sm);
      }
    });
  }

  private drawBox(): void {
    const g = this.gfx;
    const w = this.boxWidth;
    const h = this.boxHeight;
    // A small radius keeps the classic G/S near-square box look — clean, not rounded.
    const r = RADIUS.sm;
    g.clear();
    // Drop shadow for a touch of depth.
    g.fillStyle(COLORS.scrim, 0.22);
    g.fillRoundedRect(-w / 2 + 2, -h / 2 + 5, w, h, r);
    // Cream paper fill.
    g.fillStyle(COLORS.boxFill, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    // The classic G/S double border: a thick dark outer frame + a soft inner line.
    g.lineStyle(3, COLORS.boxBorder, 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    g.lineStyle(1, COLORS.boxBorderInner, 1);
    g.strokeRoundedRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, Math.max(2, r - 2));
  }
}
